import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, ShoppingCart, Plus, Edit, Trash2, LogOut, Eye, EyeOff, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    imageUrl: '',
    stockQty: '',
    sizes: '',
    colours: '',
    category: 'general',
    isActive: true
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`, {
        headers: getAuthHeader()
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      imageUrl: '',
      stockQty: '',
      sizes: '',
      colours: '',
      category: 'general',
      isActive: true
    });
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      imageUrl: product.imageUrl,
      stockQty: product.stockQty.toString(),
      sizes: product.sizes?.join(', ') || '',
      colours: product.colours?.join(', ') || '',
      category: product.category || 'general',
      isActive: product.isActive
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }

    setSaving(true);

    const productData = {
      name: formData.name,
      description: formData.description,
      price: parseFloat(formData.price),
      imageUrl: formData.imageUrl || 'https://via.placeholder.com/400',
      stockQty: parseInt(formData.stockQty) || 0,
      sizes: formData.sizes ? formData.sizes.split(',').map(s => s.trim()).filter(Boolean) : [],
      colours: formData.colours ? formData.colours.split(',').map(c => c.trim()).filter(Boolean) : [],
      category: formData.category,
      isActive: formData.isActive
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, {
          headers: getAuthHeader()
        });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, productData, {
          headers: getAuthHeader()
        });
        toast.success('Product created');
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: getAuthHeader()
      });
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#09090b] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="font-heading font-bold text-lg tracking-tight">
              G.O.A.T. Admin
            </span>
            <nav className="hidden md:flex gap-4">
              <Link
                to="/admin/dashboard"
                className="text-white font-medium text-sm uppercase tracking-wider"
              >
                Products
              </Link>
              <Link
                to="/admin/orders"
                className="text-zinc-400 hover:text-white font-medium text-sm uppercase tracking-wider"
              >
                Orders
              </Link>
              <Link
                to="/admin/subscribers"
                className="text-zinc-400 hover:text-white font-medium text-sm uppercase tracking-wider"
              >
                Subscribers
              </Link>
              <Link
                to="/admin/account"
                className="text-zinc-400 hover:text-white font-medium text-sm uppercase tracking-wider"
              >
                My Account
              </Link>
            </nav>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-zinc-400 hover:text-white"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-2xl uppercase tracking-tight flex items-center gap-3">
              <Package className="w-6 h-6" />
              Products
            </h1>
            <p className="text-zinc-500 mt-1">{products.length} products</p>
          </div>
          <Button
            onClick={openCreateDialog}
            className="bg-[#00ff9d] text-black hover:bg-[#00dd88] font-bold uppercase tracking-wider"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        </div>

        {/* Products Table */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-zinc-400" />
          </div>
        ) : (
          <div className="border border-zinc-200 overflow-hidden" data-testid="products-table">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Product</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider hidden md:table-cell">Category</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Price</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider hidden md:table-cell">Stock</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 font-bold text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {products.map(product => (
                  <tr key={product.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-zinc-100 overflow-hidden shrink-0">
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="font-medium text-sm truncate max-w-[200px]">
                          {product.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-zinc-600 hidden md:table-cell capitalize">
                      {product.category}
                    </td>
                    <td className="px-4 py-4 font-medium">
                      ${product.price.toFixed(2)}
                    </td>
                    <td className="px-4 py-4 text-sm hidden md:table-cell">
                      {product.stockQty}
                    </td>
                    <td className="px-4 py-4">
                      {product.isActive ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1">
                          <Eye className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-zinc-600 bg-zinc-100 px-2 py-1">
                          <EyeOff className="w-3 h-3" /> Hidden
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(product)}
                          data-testid={`edit-${product.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(product.id)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-${product.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Product Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              {editingProduct ? 'Edit Product' : 'Add Product'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="mt-1"
                data-testid="product-name-input"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="mt-1"
                rows={3}
                data-testid="product-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Price (AUD) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  className="mt-1"
                  data-testid="product-price-input"
                />
              </div>
              <div>
                <Label htmlFor="stockQty">Stock Quantity</Label>
                <Input
                  id="stockQty"
                  type="number"
                  value={formData.stockQty}
                  onChange={(e) => setFormData({...formData, stockQty: e.target.value})}
                  className="mt-1"
                  data-testid="product-stock-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="imageUrl">Image URL</Label>
              <Input
                id="imageUrl"
                value={formData.imageUrl}
                onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                placeholder="https://..."
                className="mt-1"
                data-testid="product-image-input"
              />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                placeholder="socks, apparel, etc."
                className="mt-1"
                data-testid="product-category-input"
              />
            </div>
            <div>
              <Label htmlFor="sizes">Sizes (comma-separated)</Label>
              <Input
                id="sizes"
                value={formData.sizes}
                onChange={(e) => setFormData({...formData, sizes: e.target.value})}
                placeholder="S, M, L, XL"
                className="mt-1"
                data-testid="product-sizes-input"
              />
            </div>
            <div>
              <Label htmlFor="colours">Colours (comma-separated)</Label>
              <Input
                id="colours"
                value={formData.colours}
                onChange={(e) => setFormData({...formData, colours: e.target.value})}
                placeholder="Black, White, Mint"
                className="mt-1"
                data-testid="product-colours-input"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({...formData, isActive: checked})}
                data-testid="product-active-switch"
              />
              <Label htmlFor="isActive">Active (visible on store)</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#09090b] text-white hover:bg-zinc-800"
              data-testid="save-product-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Product'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
